import axios from 'axios';
import router from '../router';

// 会话超时相关变量
let sessionTimeoutId = null;
const SESSION_TIMEOUT = 20 * 60 * 1000; // 20分钟，单位毫秒

// 解析JWT token（不验证签名，只解析payload）
const parseJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT解析失败:', error);
    return null;
  }
};

// 检查JWT token是否过期
const isTokenExpired = (token) => {
  if (!token) return true;
  
  const payload = parseJWT(token);
  if (!payload || !payload.exp) return true;
  
  // exp是秒级时间戳，需要转换为毫秒
  const currentTime = Math.floor(Date.now() / 1000);
  return currentTime >= payload.exp;
};

// 重置会话超时计时器
const resetSessionTimeout = () => {
  // 如果已经有计时器，先清除
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }
  
  // 只有在已登录状态下才设置超时计时器
  if (localStorage.getItem('admin_token')) {
    sessionTimeoutId = setTimeout(() => {
      console.log('会话超时，自动登出');
      // 清除token
      localStorage.removeItem('admin_token');
      // 如果当前在管理员页面，重定向到登录页
      if (router.currentRoute.value.path.includes('/admin') && 
          router.currentRoute.value.path !== '/admin/login') {
        router.push({
          path: '/admin/login',
          query: { 
            redirect: router.currentRoute.value.fullPath,
            timeout: 'true'
          }
        });
      }
    }, SESSION_TIMEOUT);
  }
};

// 初始化用户活动监听
const initActivityTracking = () => {
  // 监听用户活动事件
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  
  // 为每个事件添加监听器
  activityEvents.forEach(eventName => {
    document.addEventListener(eventName, resetSessionTimeout, true);
  });
  
  // 初始化时设置一次计时器
  resetSessionTimeout();
};

// 调试函数：显示token详细信息（控制台调试功能）
// const debugToken = (token) => {
//   if (!token) {
//     console.log('🔍 Token调试: 无token');
//     return;
//   }
//   
//   const payload = parseJWT(token);
//   if (!payload) {
//     console.log('🔍 Token调试: 无法解析token');
//     return;
//   }
//   
//   const now = Math.floor(Date.now() / 1000);
//   const exp = payload.exp;
//   const isExpired = now >= exp;
//   const timeLeft = exp - now;
//   
//   console.log('🔍 Token调试信息:', {
//     token: token.substring(0, 20) + '...',
//     payload: payload,
//     currentTime: now,
//     expiresAt: exp,
//     isExpired: isExpired,
//     timeLeft: timeLeft > 0 ? `${Math.floor(timeLeft / 60)}分钟` : '已过期',
//     timeLeftSeconds: timeLeft
//   });
// };

// 配置全局请求拦截器
axios.interceptors.request.use(
  config => {
    // 如果是管理员API路径，自动添加token
    if (config.url.includes('/api/admin/')) {
      const token = localStorage.getItem('admin_token');
      // 检查token是否过期
      if (token && !isTokenExpired(token)) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    // 每次请求也重置会话超时
    resetSessionTimeout();
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 配置全局响应拦截器
axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // 处理401未授权错误
    if (error.response && error.response.status === 401) {
      console.log('登录已过期，请重新登录');
      // 清除token
      localStorage.removeItem('admin_token');
      // 如果当前在管理员页面，重定向到登录页
      if (router.currentRoute.value.path.includes('/admin') && 
          router.currentRoute.value.path !== '/admin/login') {
        router.push({
          path: '/admin/login',
          query: { redirect: router.currentRoute.value.fullPath }
        });
      }
    }
    return Promise.reject(error);
  }
);

// 初始化会话超时监控
initActivityTracking();

// 导出认证相关工具函数
export default {
  // 检查是否已登录
  isLoggedIn() {
    const token = localStorage.getItem('admin_token');
    return !!token && !isTokenExpired(token);
  },
  
  // 检查token是否有效（包括过期检查）
  isTokenValid() {
    const token = localStorage.getItem('admin_token');
    return !!token && !isTokenExpired(token);
  },
  
  // 登录
  login(token) {
    localStorage.setItem('admin_token', token);
    // 登录时重置会话超时
    resetSessionTimeout();
  },
  
  // 登出
  logout() {
    localStorage.removeItem('admin_token');
    // 清除会话超时计时器
    if (sessionTimeoutId) {
      clearTimeout(sessionTimeoutId);
      sessionTimeoutId = null;
    }
    router.push('/admin/login');
  },
  
  // 获取token
  getToken() {
    const token = localStorage.getItem('admin_token');
    // 如果token过期，自动清除并返回null
    if (token && isTokenExpired(token)) {
      localStorage.removeItem('admin_token');
      return null;
    }
    return token;
  },
  
  // 重置会话超时（可以在其他地方手动调用）
  resetSessionTimeout,
  
  // 检查token是否过期
  isTokenExpired,
  
  // 解析JWT payload
  parseJWT,
  
  // 调试token信息（控制台调试功能）
  // debugToken
}; 